import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  makeStyles,
  Grid,
  Card,
  CardContent,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useFirebase } from "../../Firebase/FirebaseContext";
import moment from "moment";
import { collection, getDocs, orderBy, query, where, Timestamp } from "firebase/firestore";
import { subDays } from "date-fns";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Assessment, Schedule, People } from "@material-ui/icons";
import StickyHeadTable from "../Elements/Datatable";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  statsGrid: {
    marginBottom: theme.spacing(4),
  },
  statCard: {
    height: "100%",
    background: "linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)",
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 24px -10px rgba(0, 0, 0, 0.15)",
    },
  },
  statContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  statIcon: {
    fontSize: 48,
    color: theme.palette.primary.main,
    opacity: 0.8,
  },
  statValue: {
    fontWeight: 700,
    fontSize: "2rem",
    color: theme.palette.text.primary,
    lineHeight: 1,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginTop: theme.spacing(0.5),
  },
  chartCard: {
    padding: theme.spacing(3),
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
  },
  chartTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
    textAlign: "center",
  },
  tableContainer: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#FFFFFF",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    gap: theme.spacing(3),
  },
}));

const columns = [
  {
    name: "date",
    label: "Date",
    options: {
      filter: false,
      sort: true,
      customBodyRender: (value) => {
        if (value.toLocaleDateString) {
          return value.toLocaleDateString(); // Formatage de la date
        }
        return value;
      },
    },
  },
  {
    name: "salle",
    label: "Salle",
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: "lastName",
    label: "Nom",
    options: {
      filter: false,
      sort: true,
      customBodyRender: (value, tableMeta) => {
        const marital = tableMeta.rowData[tableMeta.rowData.length - 1]; // maritalName est la dernière colonne (hidden)
        if (marital && marital !== value) {
          return (
            <>
              <span style={{ fontWeight: 600 }}>{marital}</span>
              <br />
              <span style={{ fontSize: "0.8em", color: "#888" }}>né(e) {value}</span>
            </>
          );
        }
        return value;
      },
    },
  },
  {
    name: "name",
    label: "Prénom",
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: "age",
    label: "Age",
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: "sex",
    label: "Sexe",
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: "technician",
    label: "Préleveur",
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: "choice",
    label: "Type de rdv",
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: "duration",
    label: "Temps d'intervention (hh:mm:ss)",
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: "created_at",
    label: "Création de rdv",
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: "started_at",
    label: "Début de rdv",
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: "technician_came_at",
    label: "Arrivée technicien",
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: "ended_at",
    label: "Fin de rdv",
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: "waiting_duration",
    label: "Durée d’attente avant prélèvement",
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: "seen_at",
    label: "Validation secrétaire",
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: "maritalName",
    label: "Nom d'usage",
    options: {
      display: false,
      filter: false,
      sort: false,
    },
  },
];
ChartJS.register(ArcElement, Tooltip, Legend);

export default function Statistics() {
  const classes = useStyles();
  const { firebase } = useFirebase();
  const [average, setAverage] = useState();
  const [numberOfSexes, setNumberOfSexes] = useState();
  const [patientsAge, setPatientsAge] = useState();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState();
  const [filteredData, setFilteredData] = useState();

  const getDurationOfSampling = (startTime, endTime) => {
    const a = moment.unix(startTime);
    let b = moment.unix(endTime);
    let diff_s = b.diff(a, "seconds");
    let formatted = moment.utc(diff_s * 1000).format("HH:mm:ss");
    return formatted;
  };

  const getGlobalAverageDurationOfSamplings = (array) => {
    let times = [3600, 60, 1];
    let parts = array.map((s) =>
      s.split(":").reduce((s, v, i) => s + times[i] * v, 0)
    );
    let avg = Math.round(parts.reduce((a, b) => a + b, 0) / parts.length);

    return times
      .map((t) => [Math.floor(avg / t), (avg %= t)][0])
      .map((v) => v.toString().padStart(2, 0))
      .join(":");
  };

  const sexCount = (data) => {
    // Homme, Femme, Non Communiqué
    let sexes = [0, 0, 0];
    data.forEach((appointnment) => {
      if (appointnment.sex === "Homme") sexes[0]++;
      else if (appointnment.sex === "Femme") sexes[1]++;
      else sexes[2]++;
    });
    return sexes;
  };

  const ageCount = (data) => {
    const ages = Array(11).fill(0);
    const ageRanges = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

    data.forEach((appointment) => {
      const { age } = appointment;
      for (let i = 0; i < ageRanges.length; i++) {
        if (age <= ageRanges[i]) {
          ages[i]++;
          break;
        }
      }
    });

    return ages;
  };
  useEffect(() => {
    (async () => {
      const currentLab = localStorage.getItem("currentLab");
      if (!currentLab) { setLoading(false); return; }

      try {
        // Fetch rooms for this lab (with where filter)
        const roomsRef = collection(firebase.db, "rooms");
        const roomsQuery = query(roomsRef, where("laboratoryId", "==", currentLab));
        const roomsData = await getDocs(roomsQuery);
        const roomMap = {};
        roomsData.docs.forEach((item) => {
          roomMap[item.id] = item.data().name || item.id;
        });

        // Fetch appointments (last 30 days only)
        const thirtyDaysAgo = subDays(new Date(), 30);
        const appointmentsRef = collection(firebase.db, "appointements");
        const q = query(
          appointmentsRef,
          where("laboratoryId", "==", currentLab),
          where("ended_at", ">=", Timestamp.fromDate(thirtyDaysAgo)),
          orderBy("ended_at", "desc")
        );

        let times = [];
        const qSnapshot = await getDocs(q);

        let temp = [];
        qSnapshot.forEach((appointmentDoc) => {
          let docInfos = appointmentDoc.data();

          const duration = getDurationOfSampling(
            docInfos.started_at?.seconds,
            docInfos.ended_at?.seconds
          );
          const created_date = docInfos.created_at?.seconds
            ? moment.unix(docInfos.created_at.seconds).format("DD/MM/yyyy")
            : new Date(docInfos.created_at);
          const data = {
            choice: docInfos.choice,
            date: created_date,
            salle: roomMap[docInfos.roomId] || "—",
            lastName: docInfos.lastName,
            technician_came_at: moment
              .unix(docInfos.technician_came_at?.seconds)
              .format("HH:mm:ss"),
            started_at: moment
              .unix(docInfos.started_at?.seconds)
              .format("HH:mm:ss"),
            ended_at: moment
              .unix(docInfos.ended_at?.seconds)
              .format("HH:mm:ss"),
            created_at: moment
              .unix(docInfos.created_at?.seconds)
              .format("HH:mm:ss"),
            name: docInfos.name,
            age: docInfos.birthday
              ? moment().diff(moment(docInfos.birthday, "DD/MM/YYYY"), "years")
              : null,
            sex:
              (docInfos.sex === "h" && "Homme") ||
              (docInfos.sex === "f" && "Femme"),
            technician: docInfos.technician?.replace("undefined", "").trim(),
            duration: duration,
            samplingDuration: duration,
            waiting_duration: moment
              .utc(
                moment
                  .unix(docInfos.technician_came_at?.seconds)
                  .diff(moment.unix(docInfos.started_at?.seconds))
              )
              .format("HH:mm:ss"),
            seen_at: docInfos.seenAt?.seconds
              ? moment.unix(docInfos.seenAt.seconds).format("DD/MM/YYYY HH:mm")
              : (docInfos.seen ? "Traité (heure inconnue)" : "—"),
            maritalName: docInfos.maritalName || null,
          };

          times.push(data.samplingDuration);
          temp.push(data);
        });
        setNumberOfSexes(sexCount(temp));
        setPatientsAge(ageCount(temp));
        if (times.length > 0) {
          setAverage(getGlobalAverageDurationOfSamplings(times));
        }
        setAppointments(temp);
        setLoading(false);
      } catch (e) {
        console.error("Stats load error:", e);
        setLoading(false);
      }
    })();
  }, []);

  const sexPieChart = {
    labels: ["Homme", "Femme", "Non communiqué"],
    datasets: [
      {
        label: "Patients par genre",
        data: numberOfSexes,
        backgroundColor: [
          "rgba(25, 118, 210, 0.7)",
          "rgba(16, 185, 129, 0.7)",
          "rgba(156, 163, 175, 0.7)",
        ],
        borderColor: [
          "rgba(25, 118, 210, 1)",
          "rgba(16, 185, 129, 1)",
          "rgba(156, 163, 175, 1)",
        ],
        borderWidth: 2,
      },
    ],
  };

  const agePieChart = {
    labels: [
      "0-10",
      "11-20",
      "21-30",
      "31-40",
      "41-50",
      "51-60",
      "61-70",
      "71-80",
      "81-90",
      "91-100",
      "Non communiqué",
    ],
    datasets: [
      {
        label: "Patients par âge",
        data: patientsAge,
        backgroundColor: [
          "rgba(239, 68, 68, 0.7)",
          "rgba(245, 158, 11, 0.7)",
          "rgba(16, 185, 129, 0.7)",
          "rgba(59, 130, 246, 0.7)",
          "rgba(139, 92, 246, 0.7)",
          "rgba(236, 72, 153, 0.7)",
          "rgba(20, 184, 166, 0.7)",
          "rgba(234, 179, 8, 0.7)",
          "rgba(251, 146, 60, 0.7)",
          "rgba(52, 211, 153, 0.7)",
          "rgba(156, 163, 175, 0.7)",
        ],
        borderColor: [
          "rgba(239, 68, 68, 1)",
          "rgba(245, 158, 11, 1)",
          "rgba(16, 185, 129, 1)",
          "rgba(59, 130, 246, 1)",
          "rgba(139, 92, 246, 1)",
          "rgba(236, 72, 153, 1)",
          "rgba(20, 184, 166, 1)",
          "rgba(234, 179, 8, 1)",
          "rgba(251, 146, 60, 1)",
          "rgba(52, 211, 153, 1)",
          "rgba(156, 163, 175, 1)",
        ],
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: false,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 10,
          font: {
            size: 11,
            family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto',
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        borderRadius: 8,
        titleFont: {
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          size: 13,
        },
      },
    },
  };

  return (
    <div className={classes.container}>
      {!loading ? (
        <>
          <Box className={classes.header}>
            <Typography variant="h3" className={classes.title}>
              Statistiques
            </Typography>
            <Typography variant="body1" className={classes.subtitle}>
              Vue d'ensemble de l'activité
            </Typography>
          </Box>

          <Grid container spacing={3} className={classes.statsGrid}>
            <Grid item xs={12} md={4}>
              <Card className={classes.statCard} elevation={0}>
                <CardContent>
                  <Box className={classes.statContent}>
                    <Assessment className={classes.statIcon} />
                    <Box>
                      <Typography className={classes.statValue}>
                        {(filteredData && filteredData.length) ||
                          (appointments && appointments.length) ||
                          0}
                      </Typography>
                      <Typography className={classes.statLabel}>
                        Rendez-vous totaux
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card className={classes.statCard} elevation={0}>
                <CardContent>
                  <Box className={classes.statContent}>
                    <Schedule className={classes.statIcon} />
                    <Box>
                      <Typography className={classes.statValue}>
                        {average || "--:--:--"}
                      </Typography>
                      <Typography className={classes.statLabel}>
                        Temps moyen d'intervention
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card className={classes.statCard} elevation={0}>
                <CardContent>
                  <Box className={classes.statContent}>
                    <People className={classes.statIcon} />
                    <Box>
                      <Typography className={classes.statValue}>
                        {numberOfSexes
                          ? numberOfSexes.reduce((a, b) => a + b, 0)
                          : 0}
                      </Typography>
                      <Typography className={classes.statLabel}>
                        Patients traités
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper className={classes.chartCard} elevation={0}>
                <Typography variant="h5" className={classes.chartTitle}>
                  Répartition par sexe
                </Typography>
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <Pie data={sexPieChart} options={chartOptions} width={260} height={260} />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper className={classes.chartCard} elevation={0}>
                <Typography variant="h5" className={classes.chartTitle}>
                  Répartition par âge
                </Typography>
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <Pie data={agePieChart} options={chartOptions} width={260} height={260} />
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Paper className={classes.tableContainer} elevation={0}>
            <Typography
              variant="h5"
              style={{ fontWeight: 600, marginBottom: 24 }}
            >
              Détail des rendez-vous
            </Typography>
            <StickyHeadTable columns={columns} rows={appointments} />
          </Paper>
        </>
      ) : (
        <Box className={classes.loadingContainer}>
          <Assessment
            style={{ fontSize: 80, color: "#1976d2", opacity: 0.3 }}
          />
          <Typography
            variant="h5"
            style={{ fontWeight: 600, color: "#6B7280" }}
          >
            Chargement des statistiques...
          </Typography>
          <LinearProgress style={{ width: "200px", borderRadius: 4 }} />
        </Box>
      )}
    </div>
  );
}
